class HubSpotAuth {
  constructor() {
    this.clientId = process.env.HUBSPOT_CLIENT_ID || '146257293';
    this.clientSecret = process.env.HUBSPOT_CLIENT_SECRET || 'idarti';
    this.redirectUri = process.env.HUBSPOT_REDIRECT_URI || 'https://cyberdog-8wc.pages.dev/oauth-callback';
    this.scopes = [
      'oauth',
      'crm.objects.contacts.read',
      'crm.objects.companies.read',
      'crm.objects.deals.read',
      'crm.objects.owners.read',
      'crm.objects.users.read',
      'crm.objects.tickets.read'
    ];
  }

  // Generate OAuth authorization URL
  getAuthorizationUrl() {
    const baseUrl = 'https://app.hubspot.com/oauth/authorize';
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: this.scopes.join(' '),
      response_type: 'code',
      state: this.generateState()
    });

    return `${baseUrl}?${params.toString()}`;
  }

  // Generate random state for security
  generateState() {
    return Math.random().toString(36).substring(2, 15) +
           Math.random().toString(36).substring(2, 15);
  }

  // Exchange authorization code for access token
  async exchangeCodeForToken(authorizationCode) {
    const tokenUrl = 'https://api.hubapi.com/oauth/v1/token';

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.clientId,
      client_secret: this.clientSecret,
      redirect_uri: this.redirectUri,
      code: authorizationCode
    });

    try {
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Token exchange failed: ${response.status} ${errorText}`);
      }

      const tokenData = await response.json();

      // Store tokens securely (in production, use secure storage)
      this.storeTokens(tokenData);

      return tokenData;
    } catch (error) {
      console.error('OAuth token exchange failed:', error);
      throw error;
    }
  }

  // Refresh access token using refresh token
  async refreshAccessToken(refreshToken) {
    const tokenUrl = 'https://api.hubapi.com/oauth/v1/token';

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.clientId,
      client_secret: this.clientSecret,
      refresh_token: refreshToken
    });

    try {
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: body.toString()
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status}`);
      }

      const tokenData = await response.json();
      this.storeTokens(tokenData);

      return tokenData;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw error;
    }
  }

  // Store tokens (in production, use secure storage)
  storeTokens(tokenData) {
    localStorage.setItem('hubspot_access_token', tokenData.access_token);
    localStorage.setItem('hubspot_refresh_token', tokenData.refresh_token);
    localStorage.setItem('hubspot_token_expires_at',
      Date.now() + (tokenData.expires_in * 1000));
  }

  // Get stored access token
  getStoredAccessToken() {
    const token = localStorage.getItem('hubspot_access_token');
    const expiresAt = localStorage.getItem('hubspot_token_expires_at');

    if (!token || !expiresAt) {
      return null;
    }

    // Check if token is expired
    if (Date.now() >= parseInt(expiresAt)) {
      return null;
    }

    return token;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return this.getStoredAccessToken() !== null;
  }

  // Get refresh token
  getStoredRefreshToken() {
    return localStorage.getItem('hubspot_refresh_token');
  }

  // Clear stored tokens (logout)
  clearTokens() {
    localStorage.removeItem('hubspot_access_token');
    localStorage.removeItem('hubspot_refresh_token');
    localStorage.removeItem('hubspot_token_expires_at');
  }

  // Get access token, refreshing if necessary
  async getValidAccessToken() {
    let accessToken = this.getStoredAccessToken();

    if (!accessToken) {
      const refreshToken = this.getStoredRefreshToken();

      if (refreshToken) {
        try {
          const tokenData = await this.refreshAccessToken(refreshToken);
          accessToken = tokenData.access_token;
        } catch (error) {
          console.error('Failed to refresh token:', error);
          this.clearTokens();
          return null;
        }
      } else {
        return null;
      }
    }

    return accessToken;
  }

  // Use personal access token for development/testing
  usePersonalAccessToken() {
    const personalToken = process.env.HUBSPOT_PERSONAL_ACCESS_KEY;
    if (personalToken) {
      localStorage.setItem('hubspot_access_token', personalToken);
      localStorage.setItem('hubspot_token_expires_at', Date.now() + (24 * 60 * 60 * 1000)); // 24 hours
      return personalToken;
    }
    return null;
  }
}

export default HubSpotAuth;